'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    analytics?: any;
    bluemixAnalytics?: any;
    __experimentCache?: Record<string, string>;
    /** Experiment variants written by ExperimentsContext after resolution */
    __experimentVariants?: Record<string, string>;

    webExperiment?: {
      isStub?: boolean;
      isRunning?: boolean;
      getExperimentClient?: () => {
        all: () => Record<string, { key?: string; value?: string }>;
      };
    };
  }
}
let hasTrackedMetaForPath: string | null = null;

// Check if analytics is blocked due to synthetic=instana
const isAnalyticsBlocked = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('synthetic') === 'instana';
};

/**
 * Removes null, undefined, empty strings, and empty arrays
 * Used ONLY for UI metadata payload cleanup
 */
const cleanPayload = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );

const getAnonymousId = () => {
  if (typeof window !== 'undefined' && window.analytics?.user) {
    return window.analytics.user().anonymousId();
  }
  return null;
};

function trackUIInteraction(eventProperties: any, commonData: any = {}) {
  if (typeof window === 'undefined') {
    console.log('[UI Interaction] Blocked: window is undefined');
    return;
  }

  // Block tracking if synthetic=instana
  if (isAnalyticsBlocked()) {
    console.log('[UI Interaction] Blocked: synthetic=instana detected');
    return;
  }

  const STATIC_PROPERTIES = {
    productTitle: 'IBM Developer Portal',
    productCode: 'BDA0002',
    productCodeType: 'PID',
    UT30: '30M9V',
  };

  const user = commonData.user || {};
  const DYNAMIC_PROPERTIES = {
    context_page_url: window.location.hostname,
    // Renamed from userId — avoids collision with Segment's reserved top-level userId field
    ibm_user_id: user.ibmId || getAnonymousId() || 'anonymous',
    isIBMer: user.isIBMer || false,
    instanceId: user.instanceId || 'not-applicable',
    subscriptionId: user.subscriptionId || 'not-applicable',
    productPlanName: user.planName || 'N/A',
    productPlanType: user.planType || 'trial',
  };

  const CONTEXTUAL_PROPERTIES = {
    pageUrl: window.location.href,
    url: window.location.href,
    // Renamed from pagePath — matches snake_case convention of other fields
    page_path: window.location.pathname,
    pageTitle: document.title,
  };

  const webExp = window.webExperiment;

  let experimentCache: Record<string, string> = window.__experimentCache
    ? { ...window.__experimentCache }
    : {};

  if (webExp && typeof (webExp as any).getActivePages === 'function') {
    const activePages = (webExp as any).getActivePages() ?? {};

    const isWebExperimentPage = Object.keys(activePages).length > 0;

    console.log(
      '[Web Experiment] Current page:',
      window.location.pathname,
      'Active:',
      isWebExperimentPage,
      activePages,
    );

    // IMPORTANT:
    // Only resolve Web Experiment variants when
    // Amplitude says this URL is targeted.
    if (isWebExperimentPage) {
      const client =
        typeof (webExp as any).getExperimentClient === 'function'
          ? (webExp as any).getExperimentClient()
          : null;

      const allFn = client?.all ?? client?.getVariants;

      if (typeof allFn === 'function') {
        const all = allFn.call(client) ?? {};

        const treatment: Record<string, string> = {};
        const control: Record<string, string> = {};

        for (const [flagKey, variant] of Object.entries(all)) {
          const variantName = (variant as any)?.key ?? (variant as any)?.value;

          if (!variantName || variantName === 'off') {
            continue;
          }

          if (variantName === 'control') {
            control[`experiment_${flagKey}`] = variantName;
          } else {
            treatment[`experiment_${flagKey}`] = variantName;
          }
        }

        const live = Object.keys(treatment).length > 0 ? treatment : control;

        if (Object.keys(live).length > 0) {
          experimentCache = live;
        }
      }
    } else {
      // This is a non-targeted page.
      experimentCache = {};
    }
  }
  const experiments = Object.keys(experimentCache).length > 0 ? experimentCache : undefined;
  const experimentVariants = window.__experimentVariants ?? {};

  console.log('experimentVariants :::', experimentVariants);
  console.log('experiments :::', experiments);

  // Merge Web Experiment + Feature Experiment variants
  const mergedExperiments = {
    ...experimentVariants,
    ...experiments,
  };

  // Merge resolved experiments into eventProperties.payload
  const mergedEventProperties = Object.keys(mergedExperiments).length
    ? {
        ...eventProperties,
        payload: {
          ...(eventProperties.payload ?? {}),
          experiments: mergedExperiments,
        },
      }
    : eventProperties;

  const finalPayload = {
    ...STATIC_PROPERTIES,
    ...DYNAMIC_PROPERTIES,
    ...CONTEXTUAL_PROPERTIES,
    ...mergedEventProperties,
  };
  console.log(
    '[UI Interaction] Tracking event:',
    eventProperties.action,
    eventProperties.namespace,
  );
  console.log('[UI Interaction] Payload:', finalPayload);

  const attemptTrack = (retries = 0, maxRetries = 10) => {
    // Try Segment Analytics
    if (window.analytics?.track) {
      window.analytics.track('UI Interaction', finalPayload);
      console.log('[UI Interaction] ✓ Tracked via Segment Analytics');
      return;
    }

    // Retry if analytics not loaded yet (only for metadata events on initial load)
    if (retries < maxRetries && eventProperties.namespace === 'page-metadata') {
      console.log(
        `[UI Interaction] Segment not ready, retry ${retries + 1}/${maxRetries} in 200ms...`,
      );
      setTimeout(() => attemptTrack(retries + 1, maxRetries), 200);
      return;
    }

    // Give up after retries
    console.log('[UI Interaction] Blocked: window.analytics.track not available');
    console.log('[UI Interaction] To enable tracking, uncomment Segment script in layout.tsx');
  };

  attemptTrack();
}

export function trackPageViewMetaData(data: any, commonData: any = {}) {
  console.log('[Page Metadata] trackPageViewMetaData called with data:', data);

  if (typeof window === 'undefined') {
    console.log('[Page Metadata] Blocked: window is undefined');
    return;
  }

  // Block metadata tracking if synthetic=instana
  if (isAnalyticsBlocked()) {
    console.log('[Page Metadata] Blocked: synthetic=instana detected');
    return;
  }

  const currentPath = window.location.pathname;
  console.log('[Page Metadata] Current path:', currentPath);
  console.log('[Page Metadata] hasTrackedMetaForPath:', hasTrackedMetaForPath);

  // Prevent duplicate firing per route
  if (hasTrackedMetaForPath === currentPath) {
    console.log('[Page Metadata] Already tracked for path:', currentPath);
    return;
  }
  hasTrackedMetaForPath = currentPath;

  console.log('[Page Metadata] ✓ Proceeding to track metadata for path:', currentPath);
  console.log('[Page Metadata] Data being tracked:', data);

  trackUIInteraction(
    {
      action: 'viewed-metadata',
      namespace: 'page-metadata',
      pageType: data?.record_type || 'unknown',
      payload: cleanPayload({
        ...(data?.record_type && { record_type: data.record_type }),
        ...(data?.primary_category && { primary_tag: data.primary_category }),
        ...(Array.isArray(data?.authors) && data.authors.length && { author: data.authors }),

        ...(Array.isArray(data?.authors_email) && {
          author_email: data.authors_email.filter(Boolean),
        }),

        ...(data?.content_type && { contentType: data.content_type }),

        ...(data?.date_modified && { date_modified: data.date_modified }),
        ...(data?.check_date && { check_date: data.check_date }),
        ...(data?.archive_date && { archive_date: data.archive_date }),
        ...(data?.publish_date && { publish_date: data.publish_date }),

        ...(Array.isArray(data?.editors) && data.editors.length && { editors: data.editors }),

        ...(Array.isArray(data?.editors_email) &&
          data.editors_email.length && {
            editors_email: data.editors_email,
          }),

        ...(data?.like !== undefined && { like_count: data.like }),
        ...(data?.dislike !== undefined && { dislike_count: data.dislike }),
        ...(data?.favourite !== undefined && { favourite: data.favourite }),
        ...(data?.follow !== undefined && { follow: data.follow }),

        ...(data?.slug && { slug: data.slug }),
        ...(data?.status && { pageStatus: data.status }),
        ...(data?.strategic_content_area && {
          strategic_content_area: data.strategic_content_area,
        }),
        ...(data?.title && { title: data.title }),
        ...(data?.url && { url: data.url }),
        ...(data?.secondary_category && {
          all_tags: data.secondary_category,
        }),
      }),
    },
    commonData,
  );
}

// Reset metadata tracking when pathname changes
export function resetMetadataTracking() {
  hasTrackedMetaForPath = null;
  console.log('[Page Metadata] Reset tracking state');
}

export default function EngagementAnalytics() {
  const startTimeRef = useRef<number>(Date.now());
  const scrollDepthsFired = useRef<Set<number>>(new Set());

  // Populate window.__experimentCache as soon as the Amplitude CDN finishes
  // starting. The CDN uses local evaluation — it doesn't call exposureTrackingProvider
  // automatically, so we must read .all() once isRunning is true and cache the results.
  useEffect(() => {
    function snapshot(): boolean {
      const webExp = (window as any).webExperiment;

      if (!webExp || typeof webExp.getExperimentClient !== 'function') {
        return false;
      }

      // Let Amplitude decide whether the current page
      // is targeted by a Web Experiment.
      const activePages =
        typeof webExp.getActivePages === 'function' ? webExp.getActivePages() ?? {} : {};

      const isWebExperimentPage = Object.keys(activePages).length > 0;

      console.log('[Web Experiment] Active page:', isWebExperimentPage, activePages);

      // IMPORTANT:
      // Do not keep Web Experiment data on non-target pages.
      if (!isWebExperimentPage) {
        window.__experimentCache = {};
        return true;
      }

      const client = webExp.getExperimentClient();
      const allFn = client?.all ?? client?.getVariants;

      if (typeof allFn !== 'function') return false;

      const all: Record<string, any> = allFn.call(client) ?? {};

      if (Object.keys(all).length === 0) {
        window.__experimentCache = {};
        return true;
      }

      const treatment: Record<string, string> = {};
      const control: Record<string, string> = {};

      for (const [flagKey, variant] of Object.entries(all)) {
        const name = variant?.key ?? variant?.value;

        if (!name || name === 'off') continue;

        if (name === 'control') {
          control[`experiment_${flagKey}`] = name;
        } else {
          treatment[`experiment_${flagKey}`] = name;
        }
      }

      const resolved = Object.keys(treatment).length > 0 ? treatment : control;

      window.__experimentCache = resolved;

      return true;
    }

    // Fast path: CDN already running by the time React mounts
    if (snapshot()) return;

    // Poll until isRunning — typically resolves within 200–500ms
    const interval = setInterval(() => {
      if (snapshot()) {
        clearInterval(interval);
        clearTimeout(timeout);
      }
    }, 50);
    // Give up after 10s
    const timeout = setTimeout(() => clearInterval(interval), 10_000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Reset metadata tracking on mount (new page load)
  useEffect(() => {
    resetMetadataTracking();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Block all engagement analytics if synthetic=instana
    if (isAnalyticsBlocked()) {
      console.log('[Engagement Analytics] Blocked: synthetic=instana detected');
      return;
    }

    const trackTimeOnPage = () => {
      const seconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (seconds < 1) return;

      trackUIInteraction({
        action: 'capture-timing',
        namespace: 'page-timing',
        payload: { durationSeconds: seconds },
      });

      startTimeRef.current = Date.now();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackTimeOnPage();
      }
    };

    // Use pagehide instead of beforeunload to support bfcache
    // pagehide fires when the page is being unloaded or put into bfcache
    const handlePageHide = (_event: PageTransitionEvent) => {
      // Only track if page is being persisted in bfcache or unloaded
      trackTimeOnPage();
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isAnalyticsBlocked()) return;

    const DEPTHS = [25, 50, 75, 100];
    const fired = scrollDepthsFired.current;
    let ticking = false;

    const scrollEl = getScrollContainer();
    if (!scrollEl) return;

    const getScrollPercentage = () => {
      const scrollTop = scrollEl.scrollTop;
      const max = scrollEl.scrollHeight - scrollEl.clientHeight;
      if (max <= 0) return 0;
      return Math.round((scrollTop / max) * 100);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const percent = getScrollPercentage();

        DEPTHS.forEach(depth => {
          if (percent >= depth && !fired.has(depth)) {
            fired.add(depth);

            trackUIInteraction({
              action: 'scrolled',
              namespace: 'page-scrolled',
              elementId: 'page',
              payload: { scrollDepthPercentage: depth },
            });
          }
        });

        ticking = false;
      });
    };

    scrollEl.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      fired.clear();
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isAnalyticsBlocked()) return;

    const getCTAValue = (el: HTMLElement): string => {
      const ariaLabel = el.getAttribute('aria-label');
      if (ariaLabel?.trim()) return ariaLabel.trim();

      const labelledBy = el.getAttribute('aria-labelledby');
      if (labelledBy) {
        const labelEl = document.getElementById(labelledBy);
        const labelText = labelEl?.textContent?.trim();
        if (labelText) return labelText;
      }

      const dataLabel =
        el.getAttribute('data-label') ||
        el.getAttribute('data-testid') ||
        el.getAttribute('data-analytics-label');
      if (dataLabel?.trim()) return dataLabel.trim();

      const visibleText = el.innerText?.trim();
      if (visibleText) return visibleText.slice(0, 100);

      const title = el.getAttribute('title');
      if (title?.trim()) return title.trim();

      return el.id || el.className || el.tagName.toLowerCase();
    };

    const handleClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement)?.closest(
        "button,a,[role='button']",
      ) as HTMLElement | null;

      if (!el) return;

      trackUIInteraction({
        action: 'clicked',
        namespace: 'global-click',
        CTA: getCTAValue(el),
        elementId: el.id || undefined,
        type: el.tagName === 'A' ? 'Link' : 'Button',
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isAnalyticsBlocked()) return;

    const handleSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement | null;
      if (!form) return;

      const input = form.querySelector(
        "input[type='search'], input[name='q'], input[name*='search']",
      );

      if (!(input instanceof HTMLInputElement)) return;
      if (!input.value) return;

      trackUIInteraction({
        action: 'searched',
        namespace: 'global-search',
        elementId: input.name || input.id,
        payload: { query: input.value },
      });
    };

    document.addEventListener('submit', handleSubmit, true);
    return () => document.removeEventListener('submit', handleSubmit, true);
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isAnalyticsBlocked()) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      const target = e.target as HTMLElement | null;
      if (!(target instanceof HTMLInputElement)) return;

      const isSearchInput =
        target.type === 'search' ||
        target.name?.toLowerCase().includes('search') ||
        target.placeholder?.toLowerCase().includes('search');

      if (!isSearchInput) return;
      if (!target.value?.trim()) return;

      trackUIInteraction({
        action: 'searched',
        namespace: 'global-search',
        elementId: target.name || target.id || 'header-search',
        payload: {
          query: target.value.trim(),
          location: 'header',
        },
      });
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  });

  return null;
}

function getScrollContainer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;

  const isScrollable = (el: HTMLElement) => {
    try {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY || '';
      return el.scrollHeight > el.clientHeight && /(auto|scroll|overlay)/.test(overflowY);
    } catch {
      return false;
    }
  };

  const selectors = [
    '[data-scroll-container]',
    'main',
    '#main',
    '#content',
    '#__next',
    '#root',
    '.app',
    'body',
  ];

  for (const sel of selectors) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el && isScrollable(el)) return el;
  }

  const scrollingEl = (document.scrollingElement || document.documentElement) as HTMLElement | null;

  return scrollingEl && isScrollable(scrollingEl) ? scrollingEl : scrollingEl;
}
